import React, { useCallback, useRef } from 'react';
import { UserMinus } from 'lucide-react';
import { technicianInitials } from '../utils/technicianInitials';
import './BoardMinimizedTechniciansBar.css';

const DRAG_THRESHOLD_PX = 5;

/**
 * Barra inferior: técnicos com coluna minimizada (clique restaura).
 */
export function BoardMinimizedTechniciansBar({ minimizedOrdered, getHojeCount, getCompletedCount, onRestore }) {
  const chipsRef = useRef(null);
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startScroll: 0,
    dragged: false,
  });
  const suppressClickRef = useRef(false);

  const onChipsPointerDown = useCallback((e) => {
    if (e.button !== 0 || !chipsRef.current) return;
    const pointerId = e.pointerId;
    dragRef.current = {
      active: true,
      pointerId,
      startX: e.clientX,
      startScroll: chipsRef.current.scrollLeft,
      dragged: false,
    };

    const onMove = (ev) => {
      if (ev.pointerId !== pointerId || !chipsRef.current) return;
      const d = dragRef.current;
      if (!d.active) return;
      const dx = ev.clientX - d.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) {
        d.dragged = true;
        chipsRef.current.scrollLeft = d.startScroll - dx;
      }
    };

    const onUp = (ev) => {
      if (ev.pointerId !== pointerId) return;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      const d = dragRef.current;
      if (d.dragged) suppressClickRef.current = true;
      dragRef.current = {
        active: false,
        pointerId: null,
        startX: 0,
        startScroll: 0,
        dragged: false,
      };
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }, []);

  const handleChipClick = useCallback(
    (tech) => (e) => {
      if (suppressClickRef.current) {
        e.preventDefault();
        e.stopPropagation();
        suppressClickRef.current = false;
        return;
      }
      onRestore(tech);
    },
    [onRestore]
  );

  return (
    <div className="board-min-bar" role="region" aria-label="Colunas de técnicos minimizadas">
      <div className="board-min-bar__label">
        <UserMinus size={13} strokeWidth={2} aria-hidden />
        <span>Minimizados</span>
      </div>
      <div ref={chipsRef} className="board-min-bar__chips" onPointerDown={onChipsPointerDown}>
        {minimizedOrdered.map((tech) => {
          const completed = typeof getCompletedCount === 'function' ? getCompletedCount(tech) : 0;
          return (
            <button
              key={tech}
              type="button"
              className="board-min-chip"
              onClick={handleChipClick(tech)}
              title={`Restaurar coluna: ${tech}`}
            >
              <span className="board-min-chip__avatar" aria-hidden>
                {technicianInitials(tech)}
              </span>
              <span className="board-min-chip__name">{tech}</span>
              <span className="board-min-chip__metrics">
                <span className="board-min-chip__count">{getHojeCount(tech)}</span>
                {completed > 0 ? (
                  <>
                    <span className="board-min-chip__divider" aria-hidden>
                      |
                    </span>
                    <span className="board-min-chip__completed" title="OS finalizadas">
                      {completed}
                    </span>
                  </>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
